// src/pages/admin/FaqAdmin.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { authFetch } from "../../lib/auth";

type Faq = {
  id: number;
  category: string;
  question: string;
  answer: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

const CATEGORY_OPTIONS = [
  { value: "", label: "카테고리" },
  { value: "general", label: "일반" },
  { value: "payment", label: "결제" },
  { value: "fee", label: "수수료" },
  { value: "cancel", label: "취소/환불" },
  { value: "contract", label: "계약/서류" },
  { value: "etc", label: "기타" },
];

function catLabel(c: string) {
  const f = CATEGORY_OPTIONS.find((x) => x.value === c);
  return f ? f.label : c || "-";
}

function fmtDT(s: string) {
  try {
    return new Date(s).toLocaleString("ko-KR");
  } catch {
    return s;
  }
}

export default function FaqAdmin() {
  const [items, setItems] = useState<Faq[]>([]);
  const [total, setTotal] = useState(0);

  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Record<number, boolean>>({});

  // modal
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Faq | null>(null);

  const selectedIds = useMemo(
    () =>
      Object.entries(selected)
        .filter(([, v]) => v)
        .map(([k]) => Number(k)),
    [selected]
  );

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (category) qs.set("category", category);
      if (q.trim()) qs.set("q", q.trim());
      qs.set("page", "1");
      qs.set("limit", "200");

      const r = await authFetch(`/api/admin/faqs?${qs.toString()}`);
      const j = await r.json().catch(() => null);
      if (!r.ok || !j?.ok) throw new Error(j?.message || "조회 실패");

      setItems(j.items || []);
      setTotal(j.total || 0);
      setSelected({});
    } catch (e: any) {
      setError(e?.message || "조회 실패");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDeleteOne = async (id: number) => {
    if (!confirm("삭제할까요?")) return;
    try {
      const r = await authFetch(`/api/admin/faqs/${id}`, { method: "DELETE" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) throw new Error(j?.message || "삭제 실패");
      await load();
    } catch (e: any) {
      alert(e?.message || "삭제 실패");
    }
  };

  const onDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`선택 ${selectedIds.length}건을 삭제할까요?`)) return;

    try {
      for (const id of selectedIds) {
        const r = await authFetch(`/api/admin/faqs/${id}`, { method: "DELETE" });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || j?.ok === false)
          throw new Error(j?.message || `삭제 실패(id=${id})`);
      }
      await load();
    } catch (e: any) {
      alert(e?.message || "삭제 실패");
    }
  };

  const allChecked = items.length > 0 && items.every((x) => selected[x.id]);
  const someChecked = selectedIds.length > 0 && !allChecked;

  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someChecked;
    }
  }, [someChecked]);

  const toggleAll = () => {
    if (allChecked) {
      setSelected({});
    } else {
      const next: Record<number, boolean> = {};
      for (const it of items) next[it.id] = true;
      setSelected(next);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (row: Faq) => {
    setEditing(row);
    setOpen(true);
  };

  return (
    <div className="jp-admin-faq space-y-4">
      <div className="jp-admin-page-heading">
        <h1 className="text-xl font-bold text-slate-900">자주 묻는 질문 관리</h1>
      </div>

      {/* 검색/버튼 */}
      <div className="jp-admin-filter-bar mb-4 space-y-2">
        <div className="grid grid-cols-1 gap-2 sm:flex sm:items-center">
          <select
            className="h-10 w-full rounded border border-slate-300 bg-white px-3 text-sm sm:w-44"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <input
            className="h-10 w-full flex-1 rounded border border-slate-300 bg-white px-3 text-sm"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="검색어 입력"
          />

          <button
            onClick={load}
            className="h-10 w-full rounded bg-slate-800 text-sm font-semibold text-white sm:w-32"
          >
            검색
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:ml-auto sm:flex sm:w-auto sm:justify-end">
          <button
            onClick={onDeleteSelected}
            className="h-10 w-full rounded border border-slate-300 bg-white text-sm font-semibold text-slate-800"
          >
            삭제
          </button>
          <button
            onClick={openCreate}
            className="h-10 w-full rounded bg-slate-800 text-sm font-semibold text-white"
          >
            등록
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* 상단 선택/카운트 바 */}
      <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
        <label className="inline-flex items-center gap-2">
          <input
            ref={selectAllRef}
            type="checkbox"
            checked={allChecked}
            onChange={toggleAll}
          />
          <span className="text-slate-700">전체 선택</span>
        </label>

        <span className="text-slate-400">|</span>

        <span className="text-slate-600">
          선택 {selectedIds.length}건 / 총 {total.toLocaleString()}건
        </span>
      </div>

      {/* 카드 리스트 */}
      <div className="space-y-2">
        {loading && (
          <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-500">
            불러오는 중...
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-500">
            데이터가 없습니다.
          </div>
        )}

        {!loading &&
          items.map((row) => {
            const meta = `${catLabel(row.category)} · ${fmtDT(row.created_at)} · #${row.id}`;
            return (
              <div
                key={row.id}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={!!selected[row.id]}
                    onChange={(e) =>
                      setSelected((prev) => ({
                        ...prev,
                        [row.id]: e.target.checked,
                      }))
                    }
                  />

                  <div className="min-w-0 flex-1">
                    <div className="jp-admin-faq-row flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 break-words">
                          {row.question}
                        </div>

                        <div className="mt-1 text-xs text-slate-500 break-words">
                          {meta}
                          {!row.is_active && (
                            <span className="ml-2 inline-flex rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                              비노출
                            </span>
                          )}
                        </div>

                        <div className="mt-2 text-sm text-slate-700 break-words line-clamp-2">
                          {row.answer}
                        </div>
                      </div>

                      <div className="jp-admin-faq-actions flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">
                        <button
                          onClick={() => openEdit(row)}
                          className="h-9 w-20 rounded bg-slate-800 text-xs font-semibold text-white"
                        >
                          수정
                        </button>
                        <button
                          onClick={() => onDeleteOne(row.id)}
                          className="h-9 w-20 rounded bg-rose-600 text-xs font-semibold text-white"
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {open && (
        <FaqModal
          initial={editing}
          onClose={() => setOpen(false)}
          onSaved={async () => {
            setOpen(false);
            await load();
          }}
        />
      )}
    </div>
  );
}

function FaqModal({
  initial,
  onClose,
  onSaved,
}: {
  initial: Faq | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!initial?.id;

  const [category, setCategory] = useState(initial?.category || "general");
  const [question, setQuestion] = useState(initial?.question || "");
  const [answer, setAnswer] = useState(initial?.answer || "");
  const [sortOrder, setSortOrder] = useState<number>(initial?.sort_order ?? 0);
  const [isActive, setIsActive] = useState<boolean>(initial?.is_active ?? true);

  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!question.trim() || !answer.trim()) {
      alert("질문/답변은 필수입니다.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        category,
        question: question.trim(),
        answer: answer.trim(),
        sort_order: sortOrder,
        is_active: isActive,
      };

      const r = await authFetch(
        isEdit ? `/api/admin/faqs/${initial!.id}` : "/api/admin/faqs",
        {
          method: isEdit ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const j = await r.json().catch(() => null);
      if (!r.ok || !j?.ok) throw new Error(j?.message || "저장 실패");
      onSaved();
    } catch (e: any) {
      alert(e?.message || "저장 실패");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <div className="text-base font-bold text-slate-900">
            {isEdit ? "FAQ 수정" : "FAQ 등록"}
          </div>
          <button onClick={onClose} className="text-sm text-slate-500">
            닫기
          </button>
        </div>

        <div className="grid gap-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div className="sm:col-span-1">
              <label className="text-xs font-semibold text-slate-600">
                카테고리
              </label>
              <select
                className="mt-1 h-10 w-full rounded border border-slate-300 px-3 text-sm"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {CATEGORY_OPTIONS.filter((x) => x.value !== "").map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-1">
              <label className="text-xs font-semibold text-slate-600">정렬</label>
              <input
                type="number"
                className="mt-1 h-10 w-full rounded border border-slate-300 px-3 text-sm"
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value || "0", 10))}
              />
            </div>

            <div className="sm:col-span-1">
              <label className="text-xs font-semibold text-slate-600">노출</label>
              <div className="mt-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                <span className="text-slate-700">사용자에게 표시</span>
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600">질문</label>
            <input
              className="mt-1 h-10 w-full rounded border border-slate-300 px-3 text-sm"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="질문을 입력하세요"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600">답변</label>
            <textarea
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
              rows={8}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="답변을 입력하세요"
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="h-10 w-24 rounded border border-slate-300 bg-white text-sm font-semibold"
          >
            취소
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="h-10 w-28 rounded bg-slate-800 text-sm font-semibold text-white disabled:opacity-60"
          >
            {saving ? "저장중" : "저장"}
          </button>
        </div>
      </div>
    </div>
  );
}
